import { Tool, QuestionnaireData, Recommendation } from '../../types';
import { WEIGHTS, DEFAULT_RECOMMENDATIONS, MIN_SCORE_THRESHOLD } from './constants';
import { CATEGORY_PRIORITIES } from './categories/priorities';
import { ESSENTIAL_CATEGORIES } from './exclusionRules';
import { logger } from '../logger';
import { generatePersonalizedReasons, calculateMatchScore, ReasonContext } from './reasonGenerator';

export function scoreTools(tools: Tool[], data: Partial<QuestionnaireData>): Recommendation[] {
  logger.info('Starting tool scoring', {
    totalTools: tools.length,
    availableCategories: [...new Set(tools.map(t => t.category))],
    businessType: data.businessType,
    industry: data.industry
  });

  const eligibleTools = tools;

  let scoredTools = eligibleTools.map(tool => {
    let score = 15;

    const businessType = data.businessType || 'small-business';
    const subType = data.businessSubType || '';
    const effectiveType = CATEGORY_PRIORITIES[subType] ? subType : businessType;
    const priorities = CATEGORY_PRIORITIES[effectiveType] || [];
    const essentialCategories = ESSENTIAL_CATEGORIES[effectiveType] || ESSENTIAL_CATEGORIES[businessType] || [];

    let isPrioritized = false;
    let isEssential = false;
    let businessTypeMatch = false;
    let subtypeMatch = false;
    let industryMatch = false;
    let budgetMatch = false;
    let technicalMatch = false;
    let stageMatch = false;
    let hasIntegrations = false;
    let isEasyToUse = false;

    const categoryIndex = priorities.indexOf(tool.category);
    if (categoryIndex !== -1) {
      const priorityScore = (priorities.length - categoryIndex) * 15;
      score += priorityScore;
      isPrioritized = true;
    }

    if (essentialCategories.includes(tool.category)) {
      score += WEIGHTS.ESSENTIAL_CATEGORY;
      isEssential = true;
    }

    if (tool.bestFor?.includes(data.businessType!)) {
      score += WEIGHTS.BUSINESS_TYPE;
      businessTypeMatch = true;
    } else if (tool.compatibleWith?.includes(data.businessSubType!)) {
      score += WEIGHTS.SUBTYPE;
      subtypeMatch = true;
    } else if (tool.bestFor?.some(bt => bt === 'small-business' || bt === 'startup')) {
      score += WEIGHTS.BUSINESS_TYPE * 0.5;
    }

    if (tool.industries?.includes(data.industry!)) {
      score += WEIGHTS.INDUSTRY;
      industryMatch = true;
    } else if (tool.industries?.some(i =>
      (i as string) === 'all' ||
      areIndustriesRelated(i, data.industry!)
    )) {
      score += WEIGHTS.INDUSTRY * 0.5;
    }

    const matchingGoals = data.goals?.filter(goal => tool.goals?.includes(goal)) || [];
    if (matchingGoals.length > 0) {
      const baseGoalScore = Math.min(matchingGoals.length, 2) * (WEIGHTS.GOALS / 2);
      const bonusGoalScore = Math.max(0, matchingGoals.length - 2) * 20;
      score += baseGoalScore + bonusGoalScore;
    }

    const matchingFeatures = data.features?.filter(feature => tool.features?.includes(feature)) || [];
    if (matchingFeatures.length > 0) {
      const baseFeatureScore = Math.min(matchingFeatures.length, 2) * (WEIGHTS.FEATURES / 2);
      const bonusFeatureScore = Math.max(0, matchingFeatures.length - 2) * 20;
      score += baseFeatureScore + bonusFeatureScore;
    }

    // Enhanced budget scoring with tiered matching
    if (data.budget && tool.pricing && tool.pricing.length > 0) {
      const budgetTiers = ['free', 'low', 'medium', 'high'] as const;
      const userBudgetIndex = budgetTiers.indexOf(data.budget);

      // Check for exact match first
      if (tool.pricing.includes(data.budget)) {
        score += WEIGHTS.BUDGET;
        budgetMatch = true;
      } else {
        // Give partial credit for tools within budget but not exact match
        const hasLowerOption = tool.pricing.some(price => {
          const priceIndex = budgetTiers.indexOf(price);
          return priceIndex !== -1 && priceIndex < userBudgetIndex;
        });
        if (hasLowerOption) {
          score += WEIGHTS.BUDGET * 0.6;
          budgetMatch = true;
        }
      }

    }

    if (tool.technicalLevel?.includes(data.technicalExpertise!)) {
      score += WEIGHTS.TECHNICAL_MATCH;
      technicalMatch = true;
    }

    if (tool.idealStages?.includes(data.businessStage!)) {
      score += WEIGHTS.STAGE;
      stageMatch = true;
    }

    if (data.workEnvironment && tool.workEnvironments?.length) {
      if (tool.workEnvironments.includes(data.workEnvironment)) {
        score += 20;
      }
    }

    if (data.customerModel && tool.customerModels?.length) {
      if (tool.customerModels.includes(data.customerModel)) {
        score += 20;
      }
    }

    if (data.locationType && tool.locationTypes?.length) {
      if (tool.locationTypes.includes(data.locationType)) {
        score += 15;
      }
    }

    if (tool.minTeamSize) {
      const sizeOrder = ['solo', 'small', 'medium', 'large'] as const;
      const currentSize = data.teamSize || 'solo';
      const expectedSize = data.expectedTeamSize || currentSize;
      const isEarlyStage = data.businessStage === 'idea' || data.businessStage === 'launching';
      const effectiveSize = isEarlyStage && sizeOrder.indexOf(expectedSize) > sizeOrder.indexOf(currentSize)
        ? expectedSize
        : currentSize;
      const userIdx = sizeOrder.indexOf(effectiveSize);
      const toolIdx = sizeOrder.indexOf(tool.minTeamSize);
      if (userIdx >= toolIdx) {
        score += 25;
      } else {
        score -= 30;
      }
    }

    if (tool.features?.includes('integrations')) {
      const integrationScore = calcIntegrationScore(tool, eligibleTools);
      score += integrationScore;
      if (integrationScore > 15) {
        hasIntegrations = true;
      }
    }

    if (data.technicalExpertise === 'beginner' && tool.features?.includes('ease-of-use')) {
      score += 15;
      isEasyToUse = true;
    }

    const marketingCategories = ['Social Media Management', 'Marketing', 'Digital Marketing', 'SEO', 'Content Marketing', 'Marketing Analytics', 'Marketing Automation'];
    const isMarketingCoreType = data.businessType === 'side-hustler' || data.businessSubType === 'content-creator';
    if (marketingCategories.includes(tool.category) && !isMarketingCoreType) {
      const hasMarketingGoals = data.goals?.some(g =>
        ['improve-marketing', 'build-brand', 'expand-customer-base'].includes(g)
      );
      const hasMarketingFeatures = data.features?.some(f =>
        ['marketing', 'social-media', 'analytics'].includes(f as string)
      );
      if (!hasMarketingGoals && !hasMarketingFeatures) {
        score -= 60;
      }
    }

    const reasonContext: ReasonContext = {
      matchedGoals: matchingGoals,
      matchedFeatures: matchingFeatures,
      businessTypeMatch,
      subtypeMatch,
      industryMatch,
      budgetMatch,
      technicalMatch,
      stageMatch,
      isEssential,
      isPrioritized,
      hasIntegrations,
      isEasyToUse
    };

    const reasons = generatePersonalizedReasons(tool, data, reasonContext);
    const matchScore = calculateMatchScore(tool, data, score);

    return {
      tool,
      score,
      matchScore,
      reasons
    };
  });

  // Log category distribution
  const categoryDistribution = scoredTools.reduce((acc, tool) => {
    acc[tool.tool.category] = (acc[tool.tool.category] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  logger.info('Scored tools:', {
    totalTools: scoredTools.length,
    categoryDistribution,
    topScores: scoredTools.slice(0, 10).map(t => ({
      name: t.tool.name,
      score: t.score,
      category: t.tool.category
    }))
  });

  // Sort by score and ensure minimum threshold
  scoredTools = scoredTools
    .filter(tool => tool.score >= MIN_SCORE_THRESHOLD)
    .sort((a, b) => b.score - a.score);

  if (scoredTools.length < 8) {
    logger.warn('Not enough scored tools, adding popular options', {
      current: scoredTools.length
    });

    const defaultCategories = DEFAULT_RECOMMENDATIONS[data.businessType!] ||
                              DEFAULT_RECOMMENDATIONS['small-business'];

    const additionalTools = tools
      .filter(tool => !scoredTools.some(st => st.tool.id === tool.id))
      .sort((a, b) => {
        const aIndex = defaultCategories.indexOf(a.category);
        const bIndex = defaultCategories.indexOf(b.category);
        const aPriority = aIndex === -1 ? 100 : aIndex;
        const bPriority = bIndex === -1 ? 100 : bIndex;
        return aPriority - bPriority;
      })
      .slice(0, 12 - scoredTools.length)
      .map(tool => {
        const industryLabel = data.industry ? data.industry.replace(/-/g, ' ') : 'your industry';
        return {
          tool,
          score: 20,
          matchScore: 45,
          reasons: [
            `Popular ${tool.category.toLowerCase()} tool for ${industryLabel}`,
            'Widely used by similar businesses'
          ],
          isFallback: true
        };
      });

    scoredTools.push(...additionalTools);
  }

  logger.info('Returning scored tools to recommendation pipeline', {
    count: scoredTools.length,
    topScores: scoredTools.slice(0, 5).map(t => ({
      name: t.tool.name,
      score: t.score,
      category: t.tool.category
    }))
  });

  return scoredTools;
}

function calcIntegrationScore(tool: Tool, eligibleTools: Tool[]): number {
  let score = 0;
  const commonIntegrations = tool.commonIntegrations || [];
  
  eligibleTools.forEach(otherTool => {
    if (otherTool.id !== tool.id) {
      if (commonIntegrations.includes(otherTool.name.toLowerCase())) {
        score += 8;
      }
      // Check for platform compatibility
      if (tool.features?.includes('api') && otherTool.features?.includes('api')) {
        score += 5;
      }
    }
  });
  
  return Math.min(score, 25); // Cap integration score
}

function areIndustriesRelated(industry1: string, industry2: string): boolean {
  const relatedIndustries: Record<string, string[]> = {
    'technology': ['saas', 'software', 'it', 'startup'],
    'retail': ['e-commerce', 'commerce', 'shopping'],
    'creative': ['marketing', 'design', 'media'],
    'consulting': ['professional-services', 'business-services'],
    'trades': ['construction', 'home-services', 'maintenance'],
    'food-service': ['hospitality', 'restaurant', 'food-beverage'],
    'hospitality': ['food-service', 'restaurant', 'food-beverage'],
    'healthcare': ['medical', 'health', 'wellness'],
    'education': ['training', 'learning', 'academic'],
    'real-estate': ['property-management', 'construction']
  };
  
  const normalized1 = industry1.toLowerCase();
  const normalized2 = industry2.toLowerCase();
  
  return relatedIndustries[normalized1]?.includes(normalized2) ||
         relatedIndustries[normalized2]?.includes(normalized1) ||
         false;
}
